import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../Redux/userSlice';
import { INFLUENCER_BASE } from '../constants';
import {
  acceptInvitation,
  rejectInvitation,
  getInvitation,
  acceptCampaign,
  rejectCampaign
} from '../services/affiliateService';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [invitation, setInvitation] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [acceptedCampaigns, setAcceptedCampaigns] = useState(new Set());
  const [rejectedCampaigns, setRejectedCampaigns] = useState(new Set());

  const extractErrorMessage = (error) => {
    if (error?.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  };

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        dispatch(setLoading(true));
        const response = await getInvitation(token);
        setInvitation(response);
      } catch (error) {
        console.error('Accept invitation error:', error);
        const errorMsg = extractErrorMessage(error);
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchInvitation();
  }, [token, dispatch]);

  const handleAccept = async () => {
    try {
      dispatch(setLoading(true));
      const response = await acceptInvitation(token);
      console.log('Invitation accepted:', response);
      if (response.userId) {
        // Store userId if needed
        localStorage.setItem('affiliateUserId', response.userId);
      }
      toast.success('Invitation accepted successfully');
      navigate(`${INFLUENCER_BASE}/dashboard/affiliate-dashboard`);
    } catch (err) {
      const errorMsg = extractErrorMessage(err);
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleReject = async () => {
    try {
      dispatch(setLoading(true));
      await rejectInvitation(token);
      toast.success('Invitation rejected successfully');
      navigate(`${INFLUENCER_BASE}/dashboard`);
    } catch (err) {
      const errorMsg = extractErrorMessage(err);
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAcceptCampaign = async (campaignId) => {
    try {
      dispatch(setLoading(true));
      const response = await acceptCampaign(token, campaignId);
      console.log('Campaign accepted:', response);
      if (response.userId) {
        // Store userId if needed
        localStorage.setItem('affiliateUserId', response.userId);
      }
      setAcceptedCampaigns(prev => new Set([...prev, campaignId]));
      setRejectedCampaigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
      toast.success('Campaign accepted successfully');
    } catch (err) {
      const errorMsg = extractErrorMessage(err);
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRejectCampaign = async (campaignId) => {
    try {
      dispatch(setLoading(true));
      await rejectCampaign(token, campaignId);
      setRejectedCampaigns(prev => new Set([...prev, campaignId]));
      setAcceptedCampaigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
      toast.success('Campaign rejected successfully');
    } catch (err) {
      const errorMsg = extractErrorMessage(err);
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAcceptAll = async () => {
    try {
      dispatch(setLoading(true));
      for (const { campaign } of invitation.campaigns) {
        await handleAcceptCampaign(campaign._id);
      }
      await handleAccept();
    } catch (err) {
      console.error('Accept invitation error:', err);
      const errorMsg = extractErrorMessage(err);
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRejectAll = async () => {
    try {
      dispatch(setLoading(true));
      for (const { campaign } of invitation.campaigns) {
        await handleRejectCampaign(campaign._id);
      }
      await handleReject();
    } catch (err) {
      const errorMsg = extractErrorMessage(err);
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (!invitation) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Navbar />
        <div className="flex-grow container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Navbar />
      <ToastContainer position="top-right" autoClose={5000} />

      {errorMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
            <h2 className="text-lg font-semibold text-red-600 mb-2">Error</h2>
            <p className="text-gray-700 mb-4">{errorMessage}</p>
            <button
              onClick={() => {
                setErrorMessage('');
                if (errorMessage.includes('expired') || errorMessage.includes('already been')) {
                  navigate(`${INFLUENCER_BASE}/dashboard/affiliate-dashboard`);
                }
              }}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
          <div className="border-b pb-4 mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Campaign Invitation</h2>
            <p className="text-gray-600 mt-2">
              You've been invited to join as an {invitation.affiliateType} affiliate
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Your Information</h3>
            <p className="text-gray-600">{invitation.fullName}</p>
            <p className="text-gray-500">{invitation.email}</p>
            {invitation.instagramId && (
              <p className="text-gray-500 mt-1">Instagram: @{invitation.instagramId}</p>
            )}
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Campaign Details</h3>
            {invitation.campaigns?.map(({ campaign, status }) => {
              const campaignId = campaign._id;
              const commission = campaign.commission;
              const commissionText =
                commission.model === 'percentage'
                  ? `${commission.percentage}%`
                  : `${commission.currency?.symbol || ''}${commission.amount}`;

              return (
                <div key={campaignId} className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-800">{campaign.name}</h4>
                  <p className="text-sm text-gray-600">{campaign.description}</p>
                  <p className="text-sm text-gray-600">Commission: {commissionText}</p>
                  <p className="text-gray-600 mt-1">Status: {status}</p>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => handleAcceptCampaign(campaignId)}
                      disabled={acceptedCampaigns.has(campaignId) || rejectedCampaigns.has(campaignId)}
                      className={`px-3 py-1 rounded ${acceptedCampaigns.has(campaignId)
                        ? 'bg-green-100 text-green-800 cursor-not-allowed'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                        }`}
                    >
                      {acceptedCampaigns.has(campaignId) ? 'Accepted' : 'Accept'}
                    </button>
                    <button
                      onClick={() => handleRejectCampaign(campaignId)}
                      disabled={rejectedCampaigns.has(campaignId) || acceptedCampaigns.has(campaignId)}
                      className={`px-3 py-1 rounded ${rejectedCampaigns.has(campaignId)
                        ? 'bg-red-100 text-red-800 cursor-not-allowed'
                        : 'bg-red-500 hover:bg-red-600 text-white'
                        }`}
                    >
                      {rejectedCampaigns.has(campaignId) ? 'Rejected' : 'Reject'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Invitation Details</h3>
            <p className="text-gray-600">
              Invited by: {invitation.invitedBy?.email || 'Unknown sender'}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Expires: {new Date(invitation.expiresAt).toLocaleDateString()} at{' '}
              {new Date(invitation.expiresAt).toLocaleTimeString()}
            </p>
          </div>

          {invitation.paymentInfo?.method === 'bank' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Payment Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600">Method: {invitation.paymentInfo.method.toUpperCase()}</p>
                <p className="text-gray-600">Account Number: {invitation.paymentInfo.accountNumber}</p>
                <p className="text-gray-600">Bank Name: {invitation.paymentInfo.bankName}</p>
                <p className="text-gray-600">IFSC Code: {invitation.paymentInfo.ifscCode}</p>
              </div>
            </div>
          )}

          <div className="flex flex-col space-y-4">
            <div className="flex space-x-4">
              <button
                onClick={handleAcceptAll}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg"
              >
                Accept All Campaigns
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded-lg"
              >
                Reject All Campaigns
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AcceptInvite; 